import { AppstoreAddOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { useContext } from 'react';

import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';

import styles from '../ExperienceDiscount.module.scss';

import { ExperienceItem } from './ExperienceItem';
import { hasExperiencePrice } from './utils';

type ExperiencesListProps = {
  pack: ExtranetHotelPack;
  onToggle: () => void;
};

export const ExperiencesList = ({ pack }: ExperiencesListProps) => {
  const { getExperience } = useContext(ModernHotelContext);
  const { data: experienceCategories } = useExperiencesCategories();

  const publishedPackExperiences = pack.packExperiences.filter(
    (packExperience) =>
      getExperience(packExperience.experienceId)?.status ===
      ExtranetExperienceStatus.PUBLISHED
  );

  const packExperiencesWithPrice = publishedPackExperiences.filter(
    (packExperience) =>
      hasExperiencePrice(pack, getExperience(packExperience.experienceId))
  );

  const isAlwaysIncludedOvernightExperienceNames = publishedPackExperiences
    .filter(
      (packExperience) =>
        getExperience(packExperience.experienceId)?.isAlwaysIncludedWithNight ||
        !hasExperiencePrice(pack, getExperience(packExperience.experienceId))
    )
    .map((exp) => {
      return experienceCategories?.find(
        (cat) => cat.id === getExperience(exp.experienceId)?.categoryId
      )?.name;
    })
    .join(' ⸱ ');

  return (
    <div className={styles.wrapper}>
      <p className={styles.sectionTitle}>
        ACTIVITY DISCOUNTS{' '}
        <Tooltip
          title={
            <span>
              We only display the minimum price for each activity.
              <br />
              The final price will be applied to any additional guests.
            </span>
          }
        >
          <InfoCircleOutlined />
        </Tooltip>
      </p>
      {packExperiencesWithPrice.length === 0 ? (
        <EmptyState
          title="No activities found"
          icon={<AppstoreAddOutlined />}
        />
      ) : (
        <>
          {packExperiencesWithPrice.reduce<React.ReactNode[]>(
            (acc, packExperience) => {
              if (!packExperience.isIncluded) {
                return acc;
              }

              acc.push(
                <React.Fragment key={packExperience.id}>
                  <ExperienceItem pack={pack} packExperience={packExperience} />
                </React.Fragment>
              );

              return acc;
            },
            []
          )}
          {isAlwaysIncludedOvernightExperienceNames.length > 0 && (
            <div className={styles.additionalInformations}>
              + Complimentary amenities :{' '}
              {isAlwaysIncludedOvernightExperienceNames}
            </div>
          )}
        </>
      )}
    </div>
  );
};
